import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { useFormattedPrice } from 'components/secondaryMarket/shared/status';
import { WithErrorTooltip } from 'components/shared/WithErrorTooltip/WithErrorTooltip';
import { useDispatch } from 'react-redux';
import EbizQuoteStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/ebiz-quote';
import ExpiredStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/expired';
import {
  useGetSuccessAction,
  useMessages,
} from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/hooks';
import LoadingStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/loading';
import NotReadyStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/not-ready';
import OrderPlacedStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/order-placed';
import ReadyStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/ready';
import RejectedStatus from 'routes/secondary-market-batch/grid/components/operation-status-cell-renderer/rejected';
import { useRowQuoteExpiration } from 'routes/secondary-market-batch/grid/hooks/use-row-quote-expiration';
import { SecondaryCellRendererProps } from 'routes/secondary-market-batch/grid/types';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { ExposedFormStatus } from 'state/forms/models';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

function NotFoundProductLabel({ data }: { data?: RowData }) {
  const notFoundMessage = data?.metadata?.messages?.some(m => m.code === 'not-found' && m.origin === 'BCS');
  if (notFoundMessage) {
    return <span className="text-danger">{data?.bcsKey.code}</span>;
  }
}

export function OperationStatusCellRenderer({
  value,
  data,
  context,
}: SecondaryCellRendererProps<ExposedFormStatus | undefined>) {
  const { isSuccessActionEnabled, onSuccessAction, successActionLabel } = useGetSuccessAction(context.orderType, data);
  const dispatch = useDispatch();

  const { progressInPercent, remainingTimeInSeconds } = useRowQuoteExpiration({
    orderType: context.orderType,
    rowId: data?.rowId,
    operationStatus: value as ExposedFormStatus | undefined,
    endZonedDateTimeToTrade: data?.metadata.endZonedDateTimeToTrade,
    quoteTimeToTradeInSeconds: data?.metadata.timeToTradeInSeconds,
  });

  const { warnings, hasMessages, allErrors, hasBackendMessage } = useMessages(context.orderType, data);

  const { formattedNumber, formattedType } = useFormattedPrice({
    price: data?.tradingPrice ?? 0,
    priceType: data?.priceType ?? 'Percent',
    priceCurrency: data?.currency ?? '',
  });
  const price = `${formattedNumber} ${formattedType}`;

  const tradeWay = data?.tradeWay === 'ClientBuy' ? 'ask' : 'bid';

  const handleCancelQuote = () => {
    if (data?.quoteId) {
      dispatch(secondaryMarketBatchActions.clearQuoteData(data.rowId));
      logAnalytics.trackEvent('Secondary market batch', 'Cancel quote');
    }
  };

  const statusComponent = (
    <Status
      orderType={context.orderType}
      hasBackendMessage={hasBackendMessage}
      remainingTimeInSeconds={remainingTimeInSeconds}
      progressInPercent={progressInPercent}
      value={value}
      price={price}
      tradeWay={tradeWay}
      isSuccessActionEnabled={isSuccessActionEnabled}
      onSuccessAction={onSuccessAction}
      successActionLabel={successActionLabel}
      onCancelAction={handleCancelQuote}
    />
  );

  const content = (
    <div className="d-flex align-items-center gap-3 h-100">
      {statusComponent}
      <NotFoundProductLabel data={data} />
    </div>
  );

  return hasMessages ? (
    <WithErrorTooltip
      errors={allErrors}
      warnings={warnings}
      position="right"
      errorTitle="secondary-market-batch.operation.error.tooltip.title"
      canCopy
      align-items-center
      hideTooltipOnStartup
      dismissOnMouseLeave
      className="h-100"
    >
      {content}
    </WithErrorTooltip>
  ) : (
    content
  );
}

type StatusProps = {
  remainingTimeInSeconds: number;
  progressInPercent: number;
  hasBackendMessage: boolean;
  value: ExposedFormStatus | undefined | null;
  orderType: OrderType;
  price: string;
  tradeWay: string;
  isSuccessActionEnabled: boolean;
  successActionLabel: string;
  onSuccessAction?: () => void;
  onCancelAction?: () => void;
};

function Status({
  progressInPercent,
  remainingTimeInSeconds,
  hasBackendMessage,
  value,
  orderType,
  price,
  tradeWay,
  isSuccessActionEnabled,
  successActionLabel,
  onSuccessAction,
  onCancelAction,
}: StatusProps) {
  switch (value) {
    case 'NotReady':
      return <NotReadyStatus hasBackendMessage={hasBackendMessage} />;
    case 'Ready':
      return <ReadyStatus hasBackendMessage={hasBackendMessage} />;
    case 'Loading':
    case 'InProgress':
      return <LoadingStatus hasBackendMessage={hasBackendMessage} />;
    case 'Rejected':
      return <RejectedStatus hasBackendMessage={hasBackendMessage} />;
    case 'Expired':
      return <ExpiredStatus hasBackendMessage={hasBackendMessage} />;
    case 'Success':
      return (
        <OrderPlacedStatus
          orderType={orderType}
          hasBackendMessage={hasBackendMessage}
          isDisabled={!isSuccessActionEnabled}
          onClick={onSuccessAction}
          btnLabel={successActionLabel}
        />
      );
    case 'Quoted':
      return (
        <EbizQuoteStatus
          price={price}
          remainingTimeInSeconds={remainingTimeInSeconds}
          progressInPercent={progressInPercent}
          hasBackendMessage={hasBackendMessage}
          tradeWay={tradeWay}
          onCancelAction={onCancelAction}
        />
      );
    default:
      return <NotReadyStatus hasBackendMessage={hasBackendMessage} />;
  }
}
